import Markdown from '@/components/ui/Markdown';
import StatusBadge from '@/components/ui/StatusBadge';
import PhaseDescriptionAttachments from '@/components/module/projects/milestone_tab/PhaseDescriptionAttachments';
import {
    formatSprintDeadlineRange,
    formatSprintStatusLabel,
    getSprintDescriptionText,
    getSprintDisplayLabel,
    getSprintProgressPercent,
    getSprintAttachmentList,
} from '@hooks/projects/projectHelpers';
import { MutedText } from '@/pages/inside/tenant/style';
import {
    BoardDetailBlockBody,
    BoardDetailBlockTitle,
    BoardDetailKeyLabel,
    BoardDetailMetaRow,
    BoardDetailMetaStack,
    BoardDetailMetaValue,
    BoardDetailProgressBarTrack,
    BoardDetailProgressPercent,
    BoardDetailProgressWrap,
    ProgressFillViolet,
} from '@/pages/inside/tenant/style';

/**
 * Sprint header block: title, description, deadline range, progress bar, status — reference layout.
 */
export default function SprintDetailSummary({ sprint, formatDateFn }) {
    const descriptionText = getSprintDescriptionText(sprint);
    const hasDescription = descriptionText.length > 0;
    const pct = getSprintProgressPercent(sprint);
    const deadlineText = formatSprintDeadlineRange(sprint, formatDateFn);
    const attachments = getSprintAttachmentList(sprint);
    const hasAttachments = attachments.length > 0;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <BoardDetailBlockTitle>{getSprintDisplayLabel(sprint)}</BoardDetailBlockTitle>
            <BoardDetailBlockBody style={{ margin: 0 }}>
                {hasDescription ? (
                    <Markdown
                        source={descriptionText}
                        variant="compact"
                        linkPrimary
                        richInlineAnchors
                        attachmentItems={attachments}
                    />
                ) : (
                    <MutedText style={{ fontSize: 14 }}>No description</MutedText>
                )}
            </BoardDetailBlockBody>
            {hasAttachments ? (
                <PhaseDescriptionAttachments
                    items={attachments}
                    style={{ marginTop: 0, marginBottom: 0 }}
                />
            ) : null}

            <BoardDetailMetaStack style={{ marginTop: 0 }}>
                <BoardDetailMetaRow>
                    <BoardDetailKeyLabel>Sprint Deadline :</BoardDetailKeyLabel>
                    <BoardDetailMetaValue>{deadlineText}</BoardDetailMetaValue>
                </BoardDetailMetaRow>

                <BoardDetailMetaRow>
                    <BoardDetailKeyLabel>Sprint Progress :</BoardDetailKeyLabel>
                    <BoardDetailMetaValue>
                        <BoardDetailProgressWrap>
                            <BoardDetailProgressBarTrack>
                                <ProgressFillViolet $percent={pct} />
                            </BoardDetailProgressBarTrack>
                            <BoardDetailProgressPercent>{pct}%</BoardDetailProgressPercent>
                        </BoardDetailProgressWrap>
                    </BoardDetailMetaValue>
                </BoardDetailMetaRow>

                <BoardDetailMetaRow>
                    <BoardDetailKeyLabel>Status :</BoardDetailKeyLabel>
                    <BoardDetailMetaValue>
                        <StatusBadge status={sprint?.status} size="xs">
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                <span style={{ fontSize: 9, lineHeight: 1 }} aria-hidden>
                                    ●
                                </span>
                                {formatSprintStatusLabel(sprint?.status)}
                            </span>
                        </StatusBadge>
                    </BoardDetailMetaValue>
                </BoardDetailMetaRow>
            </BoardDetailMetaStack>
        </div>
    );
}
